// =====================================================================================
// INVRT Radar - web console - the company journey section (V03)
//
// The two labelled regions for one company, rendered from the two SEPARATE V03 reads:
//
//   1. "Company journey" - the ordered timeline of that company's anonymous and identified
//      visits (GET /companies/journey): event order, late-arrival badges, repeat-visit
//      numbering, the person/company level chip. The timeline NEVER contains a roster
//      contact: the server does not serve one there and the parser refuses one if a future
//      regression tried.
//   2. "ICP roster contacts" - the company's list-sourced contacts (GET /companies/roster),
//      in their own region with an explicit "not the visitor" statement. A list-found
//      decision-maker is a different thing from a person who browsed the site, and this
//      surface says so in words as well as in layout.
//
// Every state is honest: loading, error (with retry), empty, and the server's own
// partial/unavailable honesty states surfaced with their reason tokens - a provider outage
// can never be mistaken for a complete or a fabricated journey.
// =====================================================================================

import { useRef } from "react";
import { PulseRequestPanel } from "@/components/pulse/PulseRequestPanel";
import { useQuery } from "@tanstack/react-query";
import { Clock3, RefreshCw } from "lucide-react";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDateTime, readString } from "@/lib/format";
import {
  fetchCompanyJourney,
  fetchCompanyRoster,
  type CompanyQuery,
  type JourneyVisitRow,
} from "@/lib/companyJourney";

export interface CompanyJourneySectionProps {
  /** Exactly one of domain / name - the company's persisted evidence key. */
  domain?: string;
  name?: string;
}

function companyQueryOf(props: CompanyJourneySectionProps): CompanyQuery {
  if (props.domain !== undefined && props.name === undefined) return { domain: props.domain };
  if (props.name !== undefined && props.domain === undefined) return { name: props.name };
  throw new Error("CompanyJourneySection needs exactly one of domain or name");
}

/** Pages on one visit, rendered as the server stored them (defensive reads, dash for absent). */
function visitPages(visit: JourneyVisitRow): string[] {
  if (!Array.isArray(visit.pages)) return [];
  const labels: string[] = [];
  for (const page of visit.pages) {
    const record = typeof page === "object" && page !== null ? (page as Record<string, unknown>) : null;
    const title = readString(record, "title");
    const url = readString(record, "full_url") ?? readString(record, "url");
    labels.push(title ?? url ?? "-");
  }
  return labels;
}

export function CompanyJourneySection(props: CompanyJourneySectionProps) {
  const api = useApiClient();
  const { orgId, session } = useSession();
  const query = companyQueryOf(props);
  const key = query.domain ?? query.name ?? "";
  const scope = `${orgId}:${session?.user.id}:${key}`;
  const freshSince = useRef({scope, at: Date.now()});
  if (freshSince.current.scope !== scope) freshSince.current = {scope, at: Date.now()};

  const journey = useQuery({
    queryKey: ["companyJourney", orgId, session?.user.id, session?.user.last_sign_in_at, key],
    queryFn: () => fetchCompanyJourney(api, query),
    staleTime: 0,
    refetchOnMount: "always",
    enabled: Boolean(orgId),
  });

  const roster = useQuery({
    queryKey: ["companyRoster", orgId, session?.user.id, session?.user.last_sign_in_at, key],
    queryFn: () => fetchCompanyRoster(api, query),
    enabled: Boolean(orgId),
  });

  return (
    <div className="flex flex-col gap-6">
      <PulseRequestPanel loading={journey.isFetching} subject={query.domain && journey.isSuccess && !journey.isFetching && journey.dataUpdatedAt >= freshSince.current.at && journey.data.company_key.domain === query.domain && journey.data.company?.domain === query.domain ? {type:"company",key:query.domain} : null} />
      <section aria-label="Company journey" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-sm font-semibold text-ink">Company journey</h3>
            <p className="text-xs text-ink-muted">
              This company's visits to your site, in the order they happened. A late-arriving
              delivery is badged, never silently reordered.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="min-h-11"
            onClick={() => {
              void journey.refetch();
              void roster.refetch();
            }}
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Refresh
          </Button>
        </div>

        {journey.isLoading ? <LoadingState label="Loading the journey…" /> : null}
        {journey.isError ? (
          <ErrorState error={journey.error} onRetry={() => void journey.refetch()} />
        ) : null}
        {journey.data ? (
          <JourneyTimeline journey={journey.data} />
        ) : null}
      </section>

      <section aria-label="ICP roster contacts" className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-sm font-semibold text-ink">ICP roster contacts</h3>
          <p className="text-xs text-ink-muted">
            Revealed from list sources. These contacts are not the visitor: nobody has claimed
            that a person on this roster is the person who browsed your site.
          </p>
        </div>
        {roster.isLoading ? <LoadingState label="Loading roster contacts…" /> : null}
        {roster.isError ? (
          <ErrorState error={roster.error} onRetry={() => void roster.refetch()} />
        ) : null}
        {roster.data ? <RosterList roster={roster.data} /> : null}
      </section>
    </div>
  );
}

function JourneyTimeline({ journey }: { journey: Awaited<ReturnType<typeof fetchCompanyJourney>> }) {
  const { honesty, totals, visits } = journey;

  if (honesty.state === "unavailable") {
    return (
      <EmptyState
        title="The journey source is unavailable right now"
        body={`No journey is shown rather than an incomplete one presented as whole. Reason: ${
          honesty.reason ?? "unavailable"
        }`}
        tone="error"
      />
    );
  }

  if (visits.length === 0) {
    return (
      <EmptyState
        title="No recorded visits for this company yet"
        body="When this company's visits arrive they will build up here, oldest first."
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {honesty.state === "partial" ? (
        <p role="note" className="text-xs text-ink-muted">
          Partial history: the read stopped at its scan bound ({honesty.reason ?? "partial"}), so
          older events may exist beyond what is shown.
        </p>
      ) : null}
      <p className="text-xs text-ink-muted">
        {totals.events} {totals.events === 1 ? "event" : "events"} across{" "}
        {totals.distinct_visits} {totals.distinct_visits === 1 ? "visit" : "visits"} -{" "}
        {totals.pages_viewed} {totals.pages_viewed === 1 ? "page" : "pages"} viewed
      </p>
      <ol aria-label="Visit timeline" className="flex flex-col gap-2">
        {visits.map((visit) => (
          <li
            key={visit.id}
            className="flex flex-col gap-1 rounded-card border border-line px-4 py-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-ink">
                <Clock3 className="h-3.5 w-3.5 text-ink-faint" aria-hidden="true" />
                {formatDateTime(visit.event_at)}
              </span>
              {visit.late ? <Badge tone="neutral">Late arrival</Badge> : null}
              {visit.visit_number > 1 ? <Badge>Repeat visit</Badge> : null}
              {visit.is_known_customer ? <Badge>Known customer</Badge> : null}
              <Badge tone={visit.identified_level === "person" ? "info" : "neutral"}>
                {visit.identified_level === "person" ? "Person (US-verified)" : "Company"}
              </Badge>
            </div>
            <ul className="flex flex-col gap-0.5 text-sm text-ink-muted">
              {visitPages(visit).map((label, index) => (
                <li key={`${visit.id}-page-${index}`}>{label}</li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </div>
  );
}

function RosterList({ roster }: { roster: Awaited<ReturnType<typeof fetchCompanyRoster>> }) {
  if (roster.honesty.state === "unavailable") {
    return (
      <EmptyState
        title="The roster source is unavailable right now"
        body={`No roster is shown rather than a partial one presented as whole. Reason: ${
          roster.honesty.reason ?? "unavailable"
        }`}
        tone="error"
      />
    );
  }

  if (roster.contacts.length === 0) {
    return (
      <EmptyState
        title="No roster contacts revealed for this company"
        body="Contacts revealed from list sources for this company will appear here, clearly kept apart from the visitor journey above."
      />
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {roster.contacts.map((contact) => {
        const name = [
          readString(contact.person, "first_name"),
          readString(contact.person, "last_name"),
        ]
          .filter(Boolean)
          .join(" ") || readString(contact.person, "full_name") || "-";
        const title = readString(contact.person, "title");
        return (
          <li
            key={contact.id}
            className="flex flex-wrap items-center gap-2 rounded-card border border-line px-4 py-3"
          >
            <Badge tone="neutral">Roster contact</Badge>
            <span className="text-sm font-medium text-ink">{name}</span>
            {title ? <span className="text-sm text-ink-muted">{title}</span> : null}
            <span className="text-xs text-ink-muted">
              Revealed {formatDateTime(contact.occurred_at)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
